/**
 * Recorte del ROI y **reducción a 400×400 por promedio de área** (004 · US1 · T028; research R-2 y
 * R-4 corregidos por la medición de T001, `implementation-notes.md`).
 *
 * > 🔴 **La reducción no es opcional.** Medido sobre la categoría `monitor` del dataset de BoofCV
 * > (fotos reales de códigos en pantalla): con el ROI a 1080p nativo `zxing-wasm` decodifica
 * > **0 de 17**; reduciendo ese mismo ROI a 400×400 con `drawImage` + `imageSmoothingQuality:
 * > 'high'` (que es un promedio de área), **17 de 17**. Un remuestreo **bilineal ingenuo no sirve**
 * > (41–94 % por aliasing). De regalo: 0.75 ms por intento en vez de 4.87 y 640 KB en vez de 4.6 MB
 * > por transferencia al worker.
 *
 * El motivo es que el decodificador tiene **techo**, no solo suelo: por encima de ~14 píxeles de
 * cámara por módulo la ruta rápida se desploma, y un ROI de 1080p bien encuadrado da ~24. La
 * ventana de trabajo es **3.5–10 px/módulo, objetivo 6**, medida **a la entrada del decodificador**
 * (es decir, después de reducir), no en la cámara.
 *
 * > 🔴 **T096 · el recorte dejó de ser fijo.** T001 dimensionó todo suponiendo que el plato llena
 * > ~75 % del marco, que es lo que pasa con un marcador en tableta montada. El negocio es otro: el
 * > marcador corre en **un teléfono a la mano**, su plato mide ~5.4 cm, y a 30 cm ocupa el 17.5 %
 * > del lado corto del fotograma. Con el recorte fijo de 0.8 que había aquí eso da **3.49
 * > px/módulo a 30 cm y 3.00 a 35 cm** — el suelo de la ventana y por debajo. Es exactamente lo
 * > que se vio en hardware: el Bootstrap Code (estático, con segundos de intentos) sí se leía y
 * > los frames de 200 ms daban `frames_observed: 0`.
 * >
 * > El recorte lo decide ahora `roi-controller.ts`: barre una escalera mientras busca y se
 * > **engancha al tamaño medido del símbolo** en cuanto lee algo. En los mismos escenarios el
 * > recorte enganchado entrega **6.00 px/módulo**. Medido en
 * > `scratchpad/decoder-bench/src/bench-adaptive-roi.mjs`.
 * >
 * > **Quien cambie el recorte cambia también el marco de enfoque**: el marco que ve el trabajador
 * > se calcula desde el mismo ROI con `roiOverlaySide`, y hay una prueba que falla si se
 * > desincronizan (`tests/unit/roi-focus-frame.test.tsx`).
 */

/** Lado, en píxeles, de la imagen que entra al decodificador. No se toca sin volver a medir. */
export const DECODER_INPUT_SIDE = 400;

/** Módulos por lado de un `FM1C` (QR versión 2, corrección M). */
export const CHALLENGE_QR_MODULES = 25;

/** Módulos por lado de un `FM1B`/`FM1A` (QR versión 3). */
export const BOOTSTRAP_QR_MODULES = 29;

/** Suelo de la ventana de trabajo del decodificador, en px de imagen por módulo (T001). */
export const PX_PER_MODULE_MIN = 3.5;

/** Techo de la ventana de trabajo: por encima de ~14 la ruta rápida se desploma (T001). */
export const PX_PER_MODULE_MAX = 10;

/** Objetivo de diseño dentro de la ventana (T001). */
export const PX_PER_MODULE_TARGET = 6;

/**
 * **Escalera de búsqueda** (T096). Fracciones del lado menor del fotograma, de la más abierta a la
 * más cerrada, con razón 1.30 entre peldaños. Mientras no se ha leído nada el bucle las recorre en
 * ida y vuelta (`roi-controller.ts`), así que cualquier plato plausible cae en la ventana de T001
 * en alguno de ellos en menos de 1.5 s.
 *
 * Por qué hace falta: con `s` = fracción del lado corto del fotograma que ocupa el **símbolo**, los
 * píxeles por módulo a la entrada valen `400·s / (fracción · 25)`. El rango de `s` que hay que
 * cubrir —plato de 5.4 cm en un teléfono a 35 cm (`s ≈ 0.11`) hasta plato de 11.7 cm en un monitor
 * a 25 cm (`s ≈ 0.46`)— es de razón ~4, y la ventana de trabajo (3.5–10) solo cubre razón 2.86:
 * **ninguna fracción fija cubre las dos puntas**. Medido en `scratchpad/decoder-bench`
 * (`bench-adaptive-roi.mjs`).
 */
export const ROI_SEARCH_STEPS: readonly number[] = [0.9, 0.69, 0.53, 0.41, 0.31, 0.24];

/** Lo que dura cada peldaño del barrido: ~9 intentos de decodificación a 30 fps. */
export const ROI_SEARCH_STEP_MS = 300;

/**
 * Suelo del recorte enganchado. Por debajo el símbolo se queda sin margen de encuadre y cualquier
 * temblor de mano lo saca del recorte. A 0.24, un símbolo de `s = 0.0525` (el marcador más pequeño
 * y lejano que tiene sentido) todavía da 3.5 px/módulo.
 */
export const ROI_FRACTION_MIN = 0.24;

/**
 * Techo del recorte enganchado. **Es la garantía estructural de no pasarse del techo de 14
 * px/módulo de T001**: a fracción 0.92 los píxeles por módulo valen `17.4·s`, así que para superar
 * 14 haría falta `s > 0.80`, es decir, un símbolo que ocupa el 80 % del lado corto del fotograma —
 * el código tocando los bordes, sin ningún margen de encuadre. En el rango físicamente posible el
 * techo no se alcanza.
 */
export const ROI_FRACTION_MAX = 0.92;

/** Sin lecturas durante este tiempo, el recorte enganchado se suelta y vuelve a barrer. Es mayor
 * que la ráfaga de 6 frames (1.2 s) para no soltarlo en mitad de una secuencia. */
export const ROI_RELOCK_MS = 1_500;

/**
 * Histéresis del reenganche: un recorte ya enganchado solo se mueve si la fracción que pide la
 * medida se desvía más de un 15 %. Es lo que impide que el marco «respire» lectura a lectura; un
 * 15 % alrededor del objetivo 6 mantiene la entrada entre 5.2 y 6.9 px/módulo.
 */
export const ROI_LOCK_HYSTERESIS = 0.15;

/**
 * Lado que debe tener el símbolo **en la imagen reducida** para caer en el objetivo de T001.
 * `25 módulos × 6 px = 150 px`. El controlador apunta a este número sea cual sea el código leído:
 * si engancha desde un Bootstrap Code (29 módulos, mismo plato), el `FM1C` que venga después
 * queda en 5.8 px/módulo —el símbolo de v2 mide `25/33` del plato contra `29/37` el de v3— y el
 * propio Bootstrap en 5.17. Las tres cifras están dentro de la ventana, así que **no hace falta
 * que Staff conozca la zona tranquila del marcador**.
 */
export const TARGET_SYMBOL_SIDE_PX = CHALLENGE_QR_MODULES * PX_PER_MODULE_TARGET;

export interface RoiRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * ROI cuadrado centrado en el fotograma, derivado del tamaño **real** del track (research R-3: el
 * tamaño pedido y el entregado no tienen por qué coincidir).
 */
export function centerRoi(frameWidth: number, frameHeight: number, fraction: number): RoiRect {
  const safeFraction = Math.min(1, Math.max(0.1, fraction));
  const side = Math.max(1, Math.floor(Math.min(frameWidth, frameHeight) * safeFraction));
  return {
    x: Math.max(0, Math.floor((frameWidth - side) / 2)),
    y: Math.max(0, Math.floor((frameHeight - side) / 2)),
    width: side,
    height: side,
  };
}

/** Lo mínimo de un `CanvasRenderingContext2D` que necesita la reducción (facilita las pruebas). */
export interface RoiContext {
  imageSmoothingEnabled: boolean;
  imageSmoothingQuality?: ImageSmoothingQuality;
  drawImage(
    source: CanvasImageSource,
    sx: number,
    sy: number,
    sw: number,
    sh: number,
    dx: number,
    dy: number,
    dw: number,
    dh: number,
  ): void;
  getImageData(sx: number, sy: number, sw: number, sh: number): ImageData;
}

/**
 * Recorta el ROI de `source` y lo **reduce** al lado del decodificador en **un solo `drawImage`**.
 *
 * `imageSmoothingQuality: 'high'` es lo que hace que el navegador use promedio de área en lugar de
 * un bilineal ingenuo: es exactamente la diferencia entre 100 % y 41–94 % de decodificación medida
 * en T001. Reducir en dos pasos, o a mano en el worker, **no** garantiza el mismo filtro.
 */
export function drawReducedRoi(
  context: RoiContext,
  source: CanvasImageSource,
  roi: RoiRect,
  side: number = DECODER_INPUT_SIDE,
): ImageData {
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';
  context.drawImage(source, roi.x, roi.y, roi.width, roi.height, 0, 0, side, side);
  return context.getImageData(0, 0, side, side);
}

/**
 * Píxeles por módulo **a la entrada del decodificador**: la métrica que decide si el símbolo se
 * puede leer (R-4). `symbolSidePx` es el lado del símbolo ya medido sobre la imagen reducida.
 */
export function pxPerModule(symbolSidePx: number, modules: number = CHALLENGE_QR_MODULES): number {
  if (modules <= 0) {
    return 0;
  }
  return symbolSidePx / modules;
}

/**
 * Píxeles por módulo previstos cuando el símbolo ocupa `frameFraction` del ROI reducido. Con
 * `DECODER_INPUT_SIDE = 400` y un `FM1C` de 25 módulos: 50 % del marco ⇒ 8 px/módulo, 30 % ⇒ 4.8,
 * 90 % ⇒ 14.4. El rango de encuadre plausible queda dentro de la ventana medida.
 */
export function expectedPxPerModule(
  frameFraction: number,
  modules: number = CHALLENGE_QR_MODULES,
  side: number = DECODER_INPUT_SIDE,
): number {
  return pxPerModule(side * frameFraction, modules);
}

/** `true` si el valor cae en la ventana de trabajo medida en T001 (3.5–10 px/módulo). */
export function isWithinDecoderWindow(value: number): boolean {
  return value >= PX_PER_MODULE_MIN && value <= PX_PER_MODULE_MAX;
}

/** Las cuatro esquinas del símbolo que devuelve el decodificador. */
export interface SymbolCorners {
  topLeft: { x: number; y: number };
  topRight: { x: number; y: number };
  bottomLeft: { x: number; y: number };
  bottomRight: { x: number; y: number };
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }): number {
  return Math.hypot(b.x - a.x, b.y - a.y);
}

/**
 * Lado medio del símbolo leído, en píxeles de la imagen **reducida**: promedio de los cuatro lados
 * del cuadrilátero que devuelve el decodificador. Alimenta `pxPerModule` para verificar en runtime
 * (telemetría, nunca decisión) que la entrada cayó en la ventana de trabajo.
 */
export function symbolSidePx(corners: SymbolCorners): number {
  const { topLeft, topRight, bottomLeft, bottomRight } = corners;
  const sides = [
    distance(topLeft, topRight),
    distance(topRight, bottomRight),
    distance(bottomRight, bottomLeft),
    distance(bottomLeft, topLeft),
  ];
  return sides.reduce((total, value) => total + value, 0) / sides.length;
}

/**
 * Fracción válida para un recorte enganchado: dentro de `[ROI_FRACTION_MIN, ROI_FRACTION_MAX]` y
 * redondeada a tres decimales (un recorte que se mueve por milésimas solo haría temblar el marco).
 */
export function clampRoiFraction(fraction: number): number {
  if (!Number.isFinite(fraction)) {
    return ROI_SEARCH_STEPS[0] ?? 0.9;
  }
  const bounded = Math.min(ROI_FRACTION_MAX, Math.max(ROI_FRACTION_MIN, fraction));
  return Math.round(bounded * 1000) / 1000;
}

/** El ROI, tal como lo usó una vuelta del bucle de captura. Es lo que el marco tiene que dibujar. */
export interface ScanRoi {
  /** Fracción del lado **menor** del fotograma. */
  fraction: number;
  /** Lado del recorte en píxeles de **cámara**. */
  sidePx: number;
  /** `true` si la fracción viene de una medida del símbolo; `false` si es un peldaño del barrido. */
  locked: boolean;
  videoWidth: number;
  videoHeight: number;
}

/**
 * Lado, en píxeles CSS, que ocupa el ROI sobre el `<video>` pintado con `object-fit: cover`.
 *
 * **Esta función es el único puente entre el recorte y el marco de enfoque de FR-002.** El
 * `<video>` cubre el viewport, así que el navegador escala el fotograma por
 * `max(ancho/vídeoAncho, alto/vídeoAlto)` y lo centra (`object-position: 50 % 50 %` por defecto).
 * Como el ROI también está centrado, su centro y el del viewport coinciden: basta con escalar el
 * lado. Devuelve `null` mientras no haya fotograma o no se pueda medir el viewport — en ese
 * momento tampoco se está recortando nada.
 */
export function roiOverlaySide(
  roi: ScanRoi | null,
  viewWidth: number,
  viewHeight: number,
): number | null {
  if (roi === null || viewWidth <= 0 || viewHeight <= 0) {
    return null;
  }
  if (roi.videoWidth <= 0 || roi.videoHeight <= 0 || roi.sidePx <= 0) {
    return null;
  }
  const scale = Math.max(viewWidth / roi.videoWidth, viewHeight / roi.videoHeight);
  return roi.sidePx * scale;
}
